import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resolveApiBaseUrl } from '../utils/apiBase';
import { usePageMeta } from '../hooks/usePageMeta';
import { trackSearch } from '../utils/analytics';
import { WINDOW_OPTIONS, windowLabel } from '../utils/window';
import { buildWebTopicUrl, buildWebFromArticleUrl, fetchClusters, fetchArticleIdsWithEntities } from '../services/graphApi';
import { PageError, PageLoading } from '../components/PageStatus';
import SearchKittBar from '../components/SearchKittBar';
import { stripHtml, truncateText } from '../utils/text';
import { formatArticleCategories } from '../utils/articleCategories';
import {
  bucketArticles,
  countDeskNewArticles,
  DESK_CATEGORY_BY_ID,
  DESK_CATEGORY_PRESETS,
  DESK_COLUMNS,
  DESK_COMPLEX_CATEGORY_PRESETS,
  DESK_SIMPLE_CATEGORY_PRESETS,
  deskWindowStyle,
  findDeskCategoryPresetByFeedCategory,
  formatDeskTime,
  isDeskArticleNew,
  resolveEffectiveDeskColumn,
  usesDefaultDeskFeedCategory,
} from './desk/deskConfig';
import {
  buildDeskSearchUrlParams,
  buildWebGraphUrl,
  deskSearchModeLabel,
  DESK_SEARCH_MODES,
  DESK_SEARCH_SIZES,
  emptyDeskColumnQueries,
  fetchColumnDefaultHybrid,
  fetchColumnRankedBrowse,
  fetchColumnSearch,
  fetchDeskHybridSearch,
  fetchDeskIncrementalColumnSearch,
  fetchDeskSeeds,
  mapArticleToDeskColumn,
  mirrorDeskColumnQueries,
  parseDeskSearchDays,
  parseDeskSearchMode,
  parseDeskSearchSize,
  resolveActiveDeskQuery,
} from './desk/deskSearch';
import {
  defaultColumnFetchPendingMap,
  readAutoSearchMap,
  readCategoryOverrideMap,
  readCollapsedMap,
  setColumnAutoSearch,
  setColumnCategoryOverride,
  writeCollapsedMap,
} from './desk/deskAgentPrefs';
import { createDeskLoadOwnership } from './desk/deskRequestOwnership';
import { updateAdminDeskSeed } from '../services/deskAdminApi';
import { useAuth } from '../contexts/AuthContext';
import './DeskPage.css';

const STRIP_INTERVAL_MS = 8000;
const REFRESH_INTERVAL_MS = 120000;
const STRIP_DOT_COUNT = 5;
const STRIP_FETCH_SIZE = 20;
const DESK_AUTO_SEARCH_MAX_TERMS = 20;

function defaultAutoSearchPendingMap() {
  return Object.fromEntries(DESK_COLUMNS.map((column) => [column.id, false]));
}

async function fetchDeskSeedsCached(apiBase, deskSeedsRef, setDeskSeeds) {
  if (deskSeedsRef.current) {
    return deskSeedsRef.current;
  }
  const seeds = await fetchDeskSeeds(apiBase);
  deskSeedsRef.current = seeds;
  setDeskSeeds(seeds);
  return seeds;
}

async function fetchIdleColumnArticles(
  apiBase,
  column,
  seeds,
  autoSearchEnabled,
  { size, days },
) {
  const terms = seeds[column.id] || [];
  if (autoSearchEnabled && terms.length > 0) {
    return fetchColumnDefaultHybrid(apiBase, column.feedCategory, terms, { size, days });
  }
  return fetchColumnRankedBrowse(apiBase, column.feedCategory, { size, days });
}

async function fetchStripArticles(apiBase, { days }) {
  const params = new URLSearchParams({
    page: '0',
    size: String(STRIP_FETCH_SIZE),
    sortBy: 'relevance',
    sortDir: 'desc',
    days: String(days),
  });
  const response = await fetch(`${apiBase}/feed?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    return [];
  }
  const data = await response.json();
  return data.content || [];
}

function DeskNewBadge() {
  return <span className="desk-badge-new">NEW</span>;
}

/** Multi-entity Web: fromArticle → top-N seeds (Intersect + Starburst). Hidden when article has no graph entities. */
function DeskWebChip({ articleId, show }) {
  if (!show || !articleId) return null;
  return (
    <Link
      to={buildWebFromArticleUrl(articleId, { layout: 'starburst', mode: 'intersect' })}
      className="desk-badge-web"
      title="Open Memetic Web with all entities from this article"
    >
      Web
    </Link>
  );
}

/** NEW (15m) left, Web right — shared by hero + terminal rows. */
function DeskRowChips({ article, showWeb }) {
  const isNew = isDeskArticleNew(article?.publishedAt);
  return (
    <span className="desk-row-chips">
      {isNew && <DeskNewBadge />}
      <DeskWebChip articleId={article?.id} show={showWeb} />
    </span>
  );
}

/** Flip to true to restore Desk Lens research rail (kept in code; hidden as Desk↔Web bridge). */
const DESK_LENS_ENABLED = false;

function DeskColumnHeader({
  column,
  baseColumn,
  newCount,
  matchCount,
  columnSearchActive,
  columnSearchPending,
  columnSearchTerm,
  onColumnSearchChange,
  onColumnSearchCommit,
  collapsed,
  onToggleCollapsed,
  autoSearchEnabled,
  autoSearchPending,
  onToggleAutoSearch,
  onCategoryPresetSelect,
  isAdmin,
  seedTerms,
  onSeedsSaved,
}) {
  const inputId = `desk-subsearch-${column.id}`;
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorDraft, setEditorDraft] = useState('');
  const [editorBusy, setEditorBusy] = useState(false);
  const [editorError, setEditorError] = useState(null);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const categoryMenuRef = useRef(null);

  useEffect(() => {
    if (!categoryMenuOpen) {
      return undefined;
    }
    const handlePointer = (event) => {
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target)) {
        setCategoryMenuOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setCategoryMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [categoryMenuOpen]);

  const openEditor = () => {
    setEditorDraft((seedTerms || []).join('\n'));
    setEditorError(null);
    setEditorOpen(true);
  };

  const selectedPreset = findDeskCategoryPresetByFeedCategory(column.feedCategory)
    || DESK_CATEGORY_PRESETS.find((preset) => preset.columnId === baseColumn.id);

  const saveEditor = async () => {
    const lines = editorDraft
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    const deduped = [];
    const seen = new Set();
    lines.forEach((line) => {
      const key = line.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(line);
      }
    });
    if (deduped.length > DESK_AUTO_SEARCH_MAX_TERMS) {
      setEditorError(`At most ${DESK_AUTO_SEARCH_MAX_TERMS} terms allowed`);
      return;
    }
    setEditorBusy(true);
    setEditorError(null);
    try {
      await updateAdminDeskSeed(column.id, deduped);
      setEditorOpen(false);
      onSeedsSaved?.(column.id, deduped);
    } catch (err) {
      setEditorError(err.message || 'Save failed');
    } finally {
      setEditorBusy(false);
    }
  };

  return (
    <header className="desk-col-header">
      <div className="desk-col-header-top">
        <div className="desk-col-header-main">
          <span className="desk-col-icon" aria-hidden="true">
            {column.icon}
          </span>
          <div className="desk-col-title-wrap" ref={categoryMenuRef}>
            <h2 className="desk-col-title">{column.label}</h2>
            <button
              type="button"
              className={`desk-col-category-caret${categoryMenuOpen ? ' is-open' : ''}`}
              aria-haspopup="listbox"
              aria-expanded={categoryMenuOpen}
              aria-label={`Change category for ${baseColumn.label}`}
              onClick={() => setCategoryMenuOpen((open) => !open)}
            >
              ▾
            </button>
            {categoryMenuOpen && (
              <ul className="desk-col-category-menu" role="listbox" aria-label="Column category">
                <li className="desk-col-category-menu-label" role="presentation">
                  Defaults
                </li>
                {DESK_COMPLEX_CATEGORY_PRESETS.map((preset) => {
                  const selected = selectedPreset?.id === preset.id
                    || column.feedCategory === preset.feedCategory;
                  return (
                    <li key={preset.id} role="option" aria-selected={selected}>
                      <button
                        type="button"
                        className={`desk-col-category-menu-item${selected ? ' is-selected' : ''}`}
                        onClick={() => {
                          onCategoryPresetSelect?.(baseColumn.id, preset);
                          setCategoryMenuOpen(false);
                        }}
                      >
                        {preset.label}
                      </button>
                    </li>
                  );
                })}
                <li className="desk-col-category-menu-sep" role="separator" />
                <li className="desk-col-category-menu-label" role="presentation">
                  Categories
                </li>
                {DESK_SIMPLE_CATEGORY_PRESETS.map((preset) => {
                  const selected = selectedPreset?.id === preset.id
                    || column.feedCategory === preset.feedCategory;
                  return (
                    <li key={preset.id} role="option" aria-selected={selected}>
                      <button
                        type="button"
                        className={`desk-col-category-menu-item${selected ? ' is-selected' : ''}`}
                        onClick={() => {
                          onCategoryPresetSelect?.(baseColumn.id, preset);
                          setCategoryMenuOpen(false);
                        }}
                      >
                        {preset.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {columnSearchActive ? (
            <span className="desk-col-match-count">{matchCount} matches</span>
          ) : (
            newCount > 0 && (
              <span className="desk-col-new-count" title="Published in the last 15 minutes">
                {newCount} new
              </span>
            )
          )}
        </div>
        <div className="desk-col-header-actions">
          {!columnSearchActive && (
            <button
              type="button"
              className={`desk-col-auto-led${
                autoSearchPending ? ' is-pending' : autoSearchEnabled ? ' is-on' : ''
              }`}
              aria-pressed={autoSearchEnabled}
              aria-busy={autoSearchPending}
              disabled={autoSearchPending}
              title="Adds curated seed terms to ranking (not shown in the search bar)"
              onClick={() => onToggleAutoSearch(column.id)}
            >
              <span className="desk-col-auto-led-dot" aria-hidden="true" />
              <span className="desk-col-auto-led-label">Auto search</span>
            </button>
          )}
          {isAdmin && !columnSearchActive && (
            <button
              type="button"
              className="desk-col-seed-edit-btn"
              title={`Edit default search terms for ${column.shortLabel}`}
              aria-label={`Edit default search terms for ${column.shortLabel}`}
              onClick={openEditor}
            >
              ✎
            </button>
          )}
          <button
            type="button"
            className="desk-col-collapse-btn"
            aria-expanded={!collapsed}
            aria-controls={`desk-col-body-${column.id}`}
            title={collapsed ? `Expand ${column.shortLabel}` : `Collapse ${column.shortLabel} to lead`}
            onClick={() => onToggleCollapsed(column.id)}
          >
            <span className="desk-col-collapse-icon" aria-hidden="true">
              {collapsed ? '▸' : '▾'}
            </span>
            <span className="desk-col-collapse-label">{collapsed ? 'Expand' : 'Collapse'}</span>
          </button>
        </div>
      </div>
      {editorOpen && (
        <div className="desk-col-seed-editor" role="dialog" aria-label={`Edit ${column.shortLabel} search terms`}>
          <label htmlFor={`desk-seed-editor-${column.id}`} className="desk-col-seed-editor-label">
            Default search terms (one per line)
          </label>
          <textarea
            id={`desk-seed-editor-${column.id}`}
            className="desk-col-seed-editor-text"
            rows={6}
            value={editorDraft}
            onChange={(event) => setEditorDraft(event.target.value)}
          />
          <div className="desk-col-seed-editor-meta">
            {editorDraft
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean).length}
            /{DESK_AUTO_SEARCH_MAX_TERMS}
          </div>
          {editorError && <p className="desk-col-seed-editor-error">{editorError}</p>}
          <div className="desk-col-seed-editor-actions">
            <button type="button" className="desk-col-seed-editor-cancel" onClick={() => setEditorOpen(false)} disabled={editorBusy}>
              Cancel
            </button>
            <button type="button" className="desk-col-seed-editor-save" onClick={saveEditor} disabled={editorBusy}>
              {editorBusy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
      <div className="desk-col-subsearch-wrap">
        <label htmlFor={inputId} className="desk-col-subsearch-label">
          Search {column.shortLabel}
        </label>
        <input
          id={inputId}
          type="text"
          className="desk-col-subsearch"
          value={columnSearchTerm}
          onChange={(event) => onColumnSearchChange(column.id, event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onColumnSearchCommit(column.id);
            }
          }}
          placeholder={`${column.shortLabel} — Enter`}
          autoComplete="off"
          enterKeyHint="search"
        />
        {columnSearchPending && (
          <span className="desk-col-subsearch-pending" aria-live="polite">
            Enter
          </span>
        )}
      </div>
    </header>
  );
}

function DeskHero({ article, windowDays, showWeb }) {
  if (!article) {
    return (
      <div className="desk-hero desk-hero--empty">
        <p className="desk-empty-copy">No lead story yet for this column.</p>
      </div>
    );
  }

  const excerpt = truncateText(stripHtml(article.summary || ''), 160);
  const cats = formatArticleCategories(article);
  const hasImage = Boolean(article.imageUrl);

  return (
    <article
      className={`desk-hero${hasImage ? ' desk-hero--with-image' : ''}`}
      style={deskWindowStyle(article, windowDays)}
    >
      <div className="desk-hero-body">
        <span className="desk-lead-label">▌ LEAD</span>
        <Link to={`/article/${article.id}`} className="desk-headline desk-headline--hero">
          {article.title}
        </Link>
        {excerpt && <p className="desk-excerpt">{excerpt}</p>}
        <div className="desk-row-meta">
          <span className="desk-source-pill">{article.source || article.feedName || 'Source'}</span>
          {cats ? (
            <span className="desk-cats" title="Story categories">
              {cats}
            </span>
          ) : null}
          <span className="desk-time">{formatDeskTime(article.publishedAt)}</span>
          <DeskRowChips article={article} showWeb={showWeb} />
        </div>
      </div>
      {hasImage ? (
        <img
          className="desk-hero-img"
          src={article.imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      ) : null}
    </article>
  );
}

function DeskTerminalRow({ article, windowDays, showWeb }) {
  const isNew = isDeskArticleNew(article.publishedAt);
  const cats = formatArticleCategories(article);

  return (
    <article
      className={`desk-terminal-row ${isNew ? 'is-new' : ''}`}
      style={deskWindowStyle(article, windowDays)}
    >
      <span className="desk-dot-live" aria-hidden="true" />
      <div className="desk-row-body">
        <Link to={`/article/${article.id}`} className="desk-headline">
          {article.title}
        </Link>
        <div className="desk-row-meta">
          <span className="desk-source-pill">{article.source || article.feedName || 'Source'}</span>
          {cats ? (
            <span className="desk-cats" title="Story categories">
              {cats}
            </span>
          ) : null}
          <span className="desk-time">{formatDeskTime(article.publishedAt)}</span>
          <DeskRowChips article={article} showWeb={showWeb} />
        </div>
      </div>
    </article>
  );
}

function DeskTimeband({ label, bandKey, articles, windowDays, webEligibleIds }) {
  if (!articles.length) return null;
  return (
    <div className="desk-band" data-band={bandKey}>
      <div className="desk-band-label">{label}</div>
      <div className="desk-terminal-feed">
        {articles.map((article) => (
          <DeskTerminalRow
            key={article.id}
            article={article}
            windowDays={windowDays}
            showWeb={webEligibleIds?.has(article.id)}
          />
        ))}
      </div>
    </div>
  );
}

function DeskColumn({
  column,
  baseColumn,
  articles,
  columnSearchActive,
  columnSearchPending,
  columnSearchQuery,
  onColumnSearchChange,
  onColumnSearchCommit,
  columnSearchTerm,
  collapsed,
  onToggleCollapsed,
  autoSearchEnabled,
  autoSearchPending,
  onToggleAutoSearch,
  onCategoryPresetSelect,
  isAdmin,
  seedTerms,
  onSeedsSaved,
  windowDays,
  webEligibleIds,
  columnFetchPending = false,
}) {
  const [hero, ...rest] = articles;
  const newCount = countDeskNewArticles(articles);
  const buckets = bucketArticles(rest);
  const bodyId = `desk-col-body-${column.id}`;

  return (
    <section
      className={`desk-col${collapsed ? ' is-collapsed' : ''}${columnFetchPending ? ' is-fetching' : ''}`}
      style={{
        '--desk-accent': column.accentColor || `var(${column.accentVar})`,
        '--desk-accent-dim': column.accentDimColor || `var(${column.accentDimVar})`,
      }}
      aria-busy={columnFetchPending || undefined}
    >
      <DeskColumnHeader
        column={column}
        baseColumn={baseColumn}
        newCount={newCount}
        matchCount={articles.length}
        columnSearchActive={columnSearchActive}
        columnSearchPending={columnSearchPending}
        columnSearchTerm={columnSearchTerm}
        onColumnSearchChange={onColumnSearchChange}
        onColumnSearchCommit={onColumnSearchCommit}
        collapsed={collapsed}
        onToggleCollapsed={onToggleCollapsed}
        autoSearchEnabled={autoSearchEnabled}
        autoSearchPending={autoSearchPending}
        onToggleAutoSearch={onToggleAutoSearch}
        onCategoryPresetSelect={onCategoryPresetSelect}
        isAdmin={isAdmin}
        seedTerms={seedTerms}
        onSeedsSaved={onSeedsSaved}
      />

      <div id={bodyId} className="desk-col-body">
        {columnFetchPending && !hero ? (
          <div className="desk-col-loading" aria-live="polite">
            Loading {column.shortLabel}…
          </div>
        ) : (
          <DeskHero
            article={hero}
            windowDays={windowDays}
            showWeb={Boolean(hero && webEligibleIds?.has(hero.id))}
          />
        )}

        {!hero && !columnFetchPending && columnSearchActive && (
          <div className="desk-col-empty">
            <p>No matches in {column.shortLabel} for “{columnSearchQuery}”.</p>
          </div>
        )}

        {!collapsed && !columnFetchPending && (
          <div className="desk-feed">
            <DeskTimeband
              label="Last hour"
              bandKey="last-hour"
              articles={buckets.hour}
              windowDays={windowDays}
              webEligibleIds={webEligibleIds}
            />
            <DeskTimeband
              label="Today"
              bandKey="today"
              articles={buckets.today}
              windowDays={windowDays}
              webEligibleIds={webEligibleIds}
            />
            <DeskTimeband
              label="Older"
              bandKey="older"
              articles={buckets.older}
              windowDays={windowDays}
              webEligibleIds={webEligibleIds}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function DeskStripDots({ count, activeIndex, onSelect }) {
  if (count <= 1) return null;
  const dots = Math.min(STRIP_DOT_COUNT, count);
  return (
    <div className="desk-strip-dots" role="tablist" aria-label="Global signal rotation">
      {Array.from({ length: dots }, (_, i) => {
        const articleIndex = Math.floor((i / dots) * count) % count;
        const isActive = articleIndex === activeIndex;
        return (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`desk-strip-dot ${isActive ? 'active' : ''}`}
            onClick={() => onSelect(articleIndex)}
            aria-label={`Show signal ${i + 1}`}
          />
        );
      })}
    </div>
  );
}

function DeskSearchMenu({ label, valueLabel, options, onSelect, disabled }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handlePointer = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointer);
    return () => document.removeEventListener('mousedown', handlePointer);
  }, [open]);

  return (
    <div className="desk-search-menu" ref={wrapRef}>
      <button
        type="button"
        className={`desk-toolbar-pill desk-toolbar-pill--dropdown is-active ${open ? 'is-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{valueLabel}</span>
        <span className="desk-toolbar-pill-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <ul className="desk-search-menu-list" role="listbox" aria-label={label}>
          {options.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={option.selected}
                className={`desk-search-menu-item ${option.selected ? 'is-selected' : ''}`}
                onClick={() => {
                  setOpen(false);
                  if (!option.selected) {
                    onSelect(option.value);
                  }
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DeskLensDrawer({ open, onToggle, clusters, clustersLoading, clustersError }) {
  return (
    <aside className={`desk-lens-drawer ${open ? 'open' : ''}`}>
      <button type="button" className="desk-lens-rail" onClick={onToggle} aria-expanded={open}>
        <span className="desk-lens-rail-label">LENS</span>
        {clusters.length > 0 && <span className="desk-lens-count">{clusters.length}</span>}
      </button>
      <div className="desk-lens-panel">
        <h3 className="desk-lens-title">Cross-view lens</h3>
        <p className="desk-lens-sub">Research topics spanning multiple desk columns.</p>
        {clustersLoading && clusters.length === 0 && (
          <p className="desk-lens-sub">Loading clusters…</p>
        )}
        {clustersError && (
          <p className="desk-lens-sub text-danger">{clustersError}</p>
        )}
        {!clustersLoading && clusters.length === 0 && !clustersError && (
          <p className="desk-lens-sub">No cross-source topics in the last 7 days yet.</p>
        )}
        {clusters.map((cluster) => (
          <div key={cluster.id} className="desk-lens-cluster">
            <div className="desk-lens-topic">
              <span className="desk-lens-entity">{cluster.label}</span>
              <span>{cluster.topic}</span>
            </div>
            {(cluster.items || []).map((item) => {
              const col = DESK_CATEGORY_BY_ID[item.category] || DESK_COLUMNS.find((c) => c.id === item.column);
              return (
                <div
                  key={`${cluster.id}-${item.articleId || item.title}`}
                  className="desk-lens-item"
                  style={{ borderLeftColor: col ? `var(${col.accentVar})` : undefined }}
                >
                  <span className="desk-lens-cat" style={{ color: col ? `var(${col.accentVar})` : undefined }}>
                    {col?.shortLabel || item.category}
                  </span>
                  {item.articleId ? (
                    <Link to={`/article/${item.articleId}`} className="desk-headline">
                      {item.title}
                    </Link>
                  ) : (
                    <span className="desk-headline desk-headline--static">{item.title}</span>
                  )}
                </div>
              );
            })}
            <Link
              to={buildWebTopicUrl(cluster, { days: 7 })}
              className="desk-lens-open-web"
            >
              Open in Web
            </Link>
          </div>
        ))}
      </div>
    </aside>
  );
}

const DeskPage = () => {
  usePageMeta({
    title: 'Desk',
    path: '/desk',
    description: 'Live news desk — categorized columns, hybrid search, and entity lens.',
  });
  const { user, authReady, isAuthenticated } = useAuth();
  const isDeskAdmin = authReady && isAuthenticated && user?.principalName === 'admin';
  const apiBase = resolveApiBaseUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPageQ = searchParams.get('q') || '';
  const initialColQ = initialPageQ ? mirrorDeskColumnQueries(initialPageQ) : emptyDeskColumnQueries();
  const [pageSearchDraft, setPageSearchDraft] = useState(initialPageQ);
  const [committedPageQ, setCommittedPageQ] = useState(initialPageQ);
  const [columnSearchDraft, setColumnSearchDraft] = useState(initialColQ);
  const [committedColQ, setCommittedColQ] = useState(initialColQ);
  const [searchSize, setSearchSize] = useState(() => parseDeskSearchSize(searchParams.get('size')));
  const [searchMode, setSearchMode] = useState(() => parseDeskSearchMode(searchParams.get('mode')));
  const [searchDays, setSearchDays] = useState(() => parseDeskSearchDays(searchParams.get('days')));
  const prevCommittedColQRef = useRef(emptyDeskColumnQueries());
  const columnDataRef = useRef({});
  const deskSeedsRef = useRef(null);
  const categoryOverrideMapRef = useRef(null);
  const effectiveColumnsRef = useRef(DESK_COLUMNS);
  /** R1-CORR-03 / C04-3 — page + per-column ownership (stale page refresh cannot overwrite preset). */
  const deskOwnershipRef = useRef(null);
  if (!deskOwnershipRef.current) {
    deskOwnershipRef.current = createDeskLoadOwnership(DESK_COLUMNS.map((c) => c.id));
  }
  /** C04-6 — transfer/reset pending flags when ownership changes (no stale finally writes). */
  const applyDeskPendingPatch = useCallback((patch) => {
    if (!patch) return;
    if (patch.clearAutoSearchPendingAll) {
      setAutoSearchPendingMap(defaultAutoSearchPendingMap());
    }
    if (patch.clearColumnFetchPending) {
      const id = patch.clearColumnFetchPending;
      setColumnFetchPendingMap((prev) => ({ ...prev, [id]: false }));
    }
  }, []);
  const pageSearchActive = committedPageQ.trim().length > 0;
  const anyColumnSearchVisible = DESK_COLUMNS.some(
    (col) => (columnSearchDraft[col.id] || '').trim().length > 0,
  );
  const anyColumnSearchActive = DESK_COLUMNS.some(
    (col) => (committedColQ[col.id] || '').trim().length > 0,
  );
  const deskSearchActive = pageSearchActive || anyColumnSearchActive;

  const [columnData, setColumnData] = useState({});
  const [stripArticles, setStripArticles] = useState([]);
  /** Article ids with ≥1 graph entity — Web chip gate (articles without entities still listed). */
  const [webEligibleIds, setWebEligibleIds] = useState(() => new Set());
  const [stripIndex, setStripIndex] = useState(0);
  const [searchMeta, setSearchMeta] = useState({ total: 0, mode: 'none' });
  const [paused, setPaused] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [lensOpen, setLensOpen] = useState(false);
  const [clusters, setClusters] = useState([]);
  const [clustersLoading, setClustersLoading] = useState(false);
  const [clustersError, setClustersError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedMap, setCollapsedMap] = useState(() => readCollapsedMap());
  const [autoSearchMap, setAutoSearchMap] = useState(() => readAutoSearchMap());
  const autoSearchMapRef = useRef(autoSearchMap);
  const [categoryOverrideMap, setCategoryOverrideMap] = useState(() => readCategoryOverrideMap());
  const [autoSearchPendingMap, setAutoSearchPendingMap] = useState(() => defaultAutoSearchPendingMap());
  /** PERF-QT-02 — per-column in-flight so columns paint independently with visible pending state */
  const [columnFetchPendingMap, setColumnFetchPendingMap] = useState(() =>
    defaultColumnFetchPendingMap(true),
  );
  const [deskSeeds, setDeskSeeds] = useState(null);

  useEffect(() => {
    autoSearchMapRef.current = autoSearchMap;
  }, [autoSearchMap]);

  const effectiveColumns = useMemo(
    () => DESK_COLUMNS.map((col) => resolveEffectiveDeskColumn(col, categoryOverrideMap[col.id])),
    [categoryOverrideMap],
  );
  categoryOverrideMapRef.current = categoryOverrideMap;
  effectiveColumnsRef.current = effectiveColumns;

  const resolveSlotColumn = useCallback(
    (columnId, overrideMap = categoryOverrideMap) => {
      const base = DESK_COLUMNS.find((c) => c.id === columnId);
      if (!base) {
        return null;
      }
      return {
        base,
        effective: resolveEffectiveDeskColumn(base, overrideMap[columnId]),
      };
    },
    [categoryOverrideMap],
  );

  const handleToggleCollapsed = useCallback((columnId) => {
    setCollapsedMap((prev) => {
      const next = { ...prev, [columnId]: !prev[columnId] };
      writeCollapsedMap(next);
      return next;
    });
  }, []);

  const handleToggleAutoSearch = useCallback(
    async (columnId) => {
      if (deskSearchActive || autoSearchPendingMap[columnId]) {
        return;
      }

      const col = deskOwnershipRef.current.beginColumnLoad(columnId);
      applyDeskPendingPatch(col.pendingPatch);
      const nextEnabled = autoSearchMap[columnId] === false;
      setAutoSearchPendingMap((prev) => ({ ...prev, [columnId]: true }));

      const nextMap = setColumnAutoSearch(columnId, nextEnabled);
      setAutoSearchMap(nextMap);

      const slot = resolveSlotColumn(columnId);
      if (!slot) {
        if (deskOwnershipRef.current.isColumnCurrent(columnId, col.columnGen)) {
          setAutoSearchPendingMap((prev) => ({ ...prev, [columnId]: false }));
        }
        return;
      }

      try {
        const seeds = await fetchDeskSeedsCached(apiBase, deskSeedsRef, setDeskSeeds);
        if (!deskOwnershipRef.current.isColumnCurrent(columnId, col.columnGen)) return;
        const articles = await fetchIdleColumnArticles(
          apiBase,
          slot.effective,
          seeds,
          nextEnabled,
          { size: searchSize, days: searchDays },
        );
        if (!deskOwnershipRef.current.isColumnCurrent(columnId, col.columnGen)) return;
        setColumnData((prev) => ({ ...prev, [columnId]: articles }));
      } catch (err) {
        if (!deskOwnershipRef.current.isColumnCurrent(columnId, col.columnGen)) return;
        console.error('Auto-search column reload failed', err);
        setError('Failed to refresh column. Try again shortly.');
      } finally {
        if (deskOwnershipRef.current.isColumnCurrent(columnId, col.columnGen)) {
          setAutoSearchPendingMap((prev) => ({ ...prev, [columnId]: false }));
        }
      }
    },
    [
      apiBase,
      applyDeskPendingPatch,
      autoSearchMap,
      autoSearchPendingMap,
      deskSearchActive,
      resolveSlotColumn,
      searchDays,
      searchSize,
    ],
  );

  const reloadIdleColumn = useCallback(
    async (columnId, autoSearchEnabled, overrideMap = categoryOverrideMap, colToken = null) => {
      const col = colToken ?? deskOwnershipRef.current.beginColumnLoad(columnId);
      if (!colToken) {
        applyDeskPendingPatch(col.pendingPatch);
      }
      const slot = resolveSlotColumn(columnId, overrideMap);
      if (!slot) {
        return;
      }
      const seeds = await fetchDeskSeedsCached(apiBase, deskSeedsRef, setDeskSeeds);
      if (!deskOwnershipRef.current.isColumnCurrent(columnId, col.columnGen)) return;
      const articles = await fetchIdleColumnArticles(
        apiBase,
        slot.effective,
        seeds,
        autoSearchEnabled,
        { size: searchSize, days: searchDays },
      );
      if (!deskOwnershipRef.current.isColumnCurrent(columnId, col.columnGen)) return;
      setColumnData((prev) => ({ ...prev, [columnId]: articles }));
    },
    [apiBase, applyDeskPendingPatch, categoryOverrideMap, resolveSlotColumn, searchDays, searchSize],
  );

  const handleCategoryPresetSelect = useCallback(
    async (columnId, preset) => {
      const base = DESK_COLUMNS.find((c) => c.id === columnId);
      if (!base || !preset) {
        return;
      }

      const col = deskOwnershipRef.current.beginColumnLoad(columnId);
      applyDeskPendingPatch(col.pendingPatch);
      const override = usesDefaultDeskFeedCategory(base, preset)
        ? null
        : {
            feedCategory: preset.feedCategory,
            label: preset.label,
            presetId: preset.id,
          };
      const nextOverrideMap = setColumnCategoryOverride(columnId, override);
      setCategoryOverrideMap(nextOverrideMap);

      setAutoSearchPendingMap((prev) => ({ ...prev, [columnId]: true }));
      try {
        const pageQ = committedPageQ.trim();
        const colQ = (committedColQ[columnId] || '').trim();
        const activeQ = pageQ || colQ;
        const slot = resolveSlotColumn(columnId, nextOverrideMap);
        if (!slot) {
          return;
        }

        if (activeQ) {
          const seedTerms =
            autoSearchMap[columnId] === true ? (deskSeedsRef.current?.[columnId] || []) : [];
          const result = await fetchColumnSearch(apiBase, slot.effective.feedCategory, activeQ, {
            size: searchSize,
            mode: searchMode,
            days: searchDays,
            seedTerms,
          });
          if (!deskOwnershipRef.current.isColumnCurrent(columnId, col.columnGen)) return;
          setColumnData((prev) => ({ ...prev, [columnId]: result.articles }));
        } else {
          await reloadIdleColumn(
            columnId,
            autoSearchMap[columnId] === true,
            nextOverrideMap,
            col,
          );
        }
      } catch (err) {
        if (!deskOwnershipRef.current.isColumnCurrent(columnId, col.columnGen)) return;
        console.error('Category override column reload failed', err);
        setError('Failed to refresh column. Try again shortly.');
      } finally {
        if (deskOwnershipRef.current.isColumnCurrent(columnId, col.columnGen)) {
          setAutoSearchPendingMap((prev) => ({ ...prev, [columnId]: false }));
        }
      }
    },
    [
      apiBase,
      applyDeskPendingPatch,
      autoSearchMap,
      committedColQ,
      committedPageQ,
      reloadIdleColumn,
      resolveSlotColumn,
      searchDays,
      searchMode,
      searchSize,
    ],
  );

  const handleSeedsSaved = useCallback(
    async (columnId, terms) => {
      deskSeedsRef.current = { ...(deskSeedsRef.current || {}), [columnId]: terms };
      setDeskSeeds((prev) => ({ ...(prev || {}), [columnId]: terms }));
      if (deskSearchActive) {
        return;
      }

      const col = deskOwnershipRef.current.beginColumnLoad(columnId);
      applyDeskPendingPatch(col.pendingPatch);
      setAutoSearchPendingMap((prev) => ({ ...prev, [columnId]: true }));
      try {
        await reloadIdleColumn(columnId, autoSearchMap[columnId] === true, undefined, col);
      } catch (err) {
        console.error('Column reload after seed save failed', err);
      } finally {
        if (deskOwnershipRef.current.isColumnCurrent(columnId, col.columnGen)) {
          setAutoSearchPendingMap((prev) => ({ ...prev, [columnId]: false }));
        }
      }
    },
    [applyDeskPendingPatch, autoSearchMap, deskSearchActive, reloadIdleColumn],
  );

  useEffect(() => {
    columnDataRef.current = columnData;
  }, [columnData]);

  useEffect(() => {
    const ids = [];
    Object.values(columnData).forEach((articles) => {
      (articles || []).forEach((article) => {
        if (article?.id) ids.push(article.id);
      });
    });
    (stripArticles || []).forEach((article) => {
      if (article?.id) ids.push(article.id);
    });
    if (ids.length === 0) {
      setWebEligibleIds(new Set());
      return undefined;
    }
    let cancelled = false;
    fetchArticleIdsWithEntities(ids)
      .then((eligible) => {
        if (!cancelled) {
          setWebEligibleIds(new Set(eligible));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWebEligibleIds(new Set());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [columnData, stripArticles]);

  const loadDeskDefault = useCallback(async () => {
    const page = deskOwnershipRef.current.beginPageLoad();
    applyDeskPendingPatch(page.pendingPatch);
    const canApply = (columnId) =>
      deskOwnershipRef.current.canApplyPageColumn(
        page.pageGen,
        columnId,
        page.columnGens[columnId],
      );
    const pageCurrent = () => deskOwnershipRef.current.isPageCurrent(page.pageGen);
    try {
      setError(null);
      setColumnFetchPendingMap(defaultColumnFetchPendingMap(true));
      // Show the 3-column grid with per-column pending (not a full-page spinner).
      setLoading(false);
      const seeds = await fetchDeskSeedsCached(apiBase, deskSeedsRef, setDeskSeeds);
      if (!pageCurrent()) return;
      const overrideMap = categoryOverrideMapRef.current || {};
      const autoMap = autoSearchMapRef.current || {};

      // Progressive column paint — fetch all 3 in parallel; clear pending as each returns.
      const columnFetches = DESK_COLUMNS.map(async (base) => {
        const override = overrideMap[base.id];
        const col = resolveEffectiveDeskColumn(base, override);
        try {
          const articles = await fetchIdleColumnArticles(
            apiBase,
            col,
            seeds,
            autoMap[base.id] === true,
            { size: searchSize, days: searchDays },
          );
          if (!canApply(base.id)) return [base.id, articles];
          setColumnData((prev) => ({ ...prev, [base.id]: articles }));
          return [base.id, articles];
        } finally {
          if (canApply(base.id)) {
            setColumnFetchPendingMap((prev) => ({ ...prev, [base.id]: false }));
          }
        }
      });
      const stripFetch = fetchStripArticles(apiBase, { days: searchDays }).then((articles) => {
        if (pageCurrent()) {
          setStripArticles(articles);
        }
        return articles;
      });

      await Promise.all([Promise.all(columnFetches), stripFetch]);
      if (!pageCurrent()) return;
      setSearchMeta({ total: 0, mode: 'none' });
      prevCommittedColQRef.current = emptyDeskColumnQueries();
    } catch (err) {
      if (!pageCurrent()) return;
      console.error('Desk load failed', err);
      setError('Failed to load desk feeds. Try again shortly.');
      setColumnFetchPendingMap(defaultColumnFetchPendingMap(false));
      setLoading(false);
    }
  }, [apiBase, applyDeskPendingPatch, searchDays, searchSize]);

  const loadDeskSearch = useCallback(async () => {
    const pageQ = committedPageQ.trim();
    const hasColQ = DESK_COLUMNS.some((col) => (committedColQ[col.id] || '').trim().length > 0);

    if (!pageQ && !hasColQ) {
      loadDeskDefault();
      return;
    }

    const page = deskOwnershipRef.current.beginPageLoad();
    applyDeskPendingPatch(page.pendingPatch);
    const canApply = (columnId) =>
      deskOwnershipRef.current.canApplyPageColumn(
        page.pageGen,
        columnId,
        page.columnGens[columnId],
      );
    const pageCurrent = () => deskOwnershipRef.current.isPageCurrent(page.pageGen);
    try {
      setLoading(true);
      setError(null);
      setColumnFetchPendingMap(defaultColumnFetchPendingMap(true));
      const seeds = await fetchDeskSeedsCached(apiBase, deskSeedsRef, setDeskSeeds);
      if (!pageCurrent()) return;
      const columnDefs = effectiveColumnsRef.current || DESK_COLUMNS;
      // Keep prior columns visible; mark pending and paint each as it returns.
      setLoading(false);
      const searchOpts = {
        sizePerColumn: searchSize,
        mode: searchMode,
        days: searchDays,
        columns: columnDefs,
        seedsByColumn: seeds,
        autoSearchMap: autoSearchMapRef.current || {},
        onColumnArticles: (columnId, articles) => {
          if (!canApply(columnId)) return;
          setColumnData((prev) => ({ ...prev, [columnId]: articles }));
          setColumnFetchPendingMap((prev) => ({ ...prev, [columnId]: false }));
        },
      };
      let result;
      if (pageQ) {
        result = await fetchDeskHybridSearch(apiBase, pageQ, searchOpts);
        if (!pageCurrent()) return;
        prevCommittedColQRef.current = mirrorDeskColumnQueries(pageQ);
      } else {
        result = await fetchDeskIncrementalColumnSearch(
          apiBase,
          committedColQ,
          prevCommittedColQRef.current,
          columnDataRef.current,
          searchOpts,
        );
        if (!pageCurrent()) return;
        prevCommittedColQRef.current = { ...committedColQ };
        // Only apply columns that were not superseded by a column-local reload mid-flight
        const next = { ...result.columns };
        DESK_COLUMNS.forEach((col) => {
          if (!canApply(col.id)) {
            delete next[col.id];
          }
        });
        setColumnData((prev) => ({ ...prev, ...next }));
        setColumnFetchPendingMap((prev) => {
          const pending = defaultColumnFetchPendingMap(false);
          DESK_COLUMNS.forEach((col) => {
            if (!canApply(col.id)) {
              pending[col.id] = prev[col.id];
            }
          });
          return pending;
        });
      }
      if (!pageCurrent()) return;
      setStripArticles([]);
      setSearchMeta({ total: result.total, mode: result.mode });
      trackSearch(pageQ || 'column-filter', result.total ?? 0, 'desk');
    } catch (err) {
      if (!pageCurrent()) return;
      console.error('Desk search failed', err);
      setError('Desk search failed. Try again or clear the query.');
      setColumnFetchPendingMap(defaultColumnFetchPendingMap(false));
      setLoading(false);
    }
  }, [
    apiBase,
    committedColQ,
    committedPageQ,
    applyDeskPendingPatch,
    loadDeskDefault,
    searchDays,
    searchMode,
    searchSize,
  ]);

  const loadClusters = useCallback(async () => {
    setClustersLoading(true);
    setClustersError(null);
    try {
      const response = await fetchClusters({ limit: 8 });
      setClusters(response?.clusters || []);
    } catch (err) {
      console.error('Lens clusters load failed', err);
      setClusters([]);
      setClustersError('Clusters unavailable');
    } finally {
      setClustersLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = buildDeskSearchUrlParams({
      q: committedPageQ,
      size: searchSize,
      mode: searchMode,
      days: searchDays,
    });
    setSearchParams(params, { replace: true });
  }, [committedPageQ, searchDays, searchMode, searchSize, setSearchParams]);

  useEffect(() => {
    if (deskSearchActive) {
      loadDeskSearch();
    } else {
      loadDeskDefault();
    }
  }, [deskSearchActive, committedPageQ, committedColQ, loadDeskDefault, loadDeskSearch, searchDays, searchMode, searchSize]);

  useEffect(() => {
    if (!DESK_LENS_ENABLED) return undefined;
    loadClusters();
    return undefined;
  }, [loadClusters]);

  useEffect(() => {
    if (paused || deskSearchActive) return undefined;
    const id = window.setInterval(() => {
      loadDeskDefault();
      if (DESK_LENS_ENABLED) {
        loadClusters();
      }
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [loadDeskDefault, loadClusters, paused, deskSearchActive]);

  useEffect(() => {
    if (paused || deskSearchActive || stripArticles.length === 0) return undefined;
    const id = window.setInterval(() => {
      setStripIndex((i) => (i + 1) % stripArticles.length);
    }, STRIP_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [paused, deskSearchActive, stripArticles.length]);

  const stripItem = stripArticles[stripIndex];
  const stripColumn = useMemo(() => {
    const deskId = stripItem ? mapArticleToDeskColumn(stripItem) : null;
    return (deskId && DESK_CATEGORY_BY_ID[deskId]) || null;
  }, [stripItem]);
  const stripStoryCats = stripItem ? formatArticleCategories(stripItem) : '';

  const commitPageSearch = () => {
    const trimmed = pageSearchDraft.trim();
    const mirrored = mirrorDeskColumnQueries(trimmed);
    setCommittedPageQ(trimmed);
    setColumnSearchDraft(mirrored);
    setCommittedColQ(mirrored);
    prevCommittedColQRef.current = emptyDeskColumnQueries();
  };

  const commitColumnSearch = (columnId) => {
    const trimmed = (columnSearchDraft[columnId] || '').trim();
    if (committedPageQ.trim() || pageSearchDraft.trim()) {
      setPageSearchDraft('');
      setCommittedPageQ('');
      const next = { ...emptyDeskColumnQueries(), [columnId]: trimmed };
      setColumnSearchDraft({ ...emptyDeskColumnQueries(), [columnId]: columnSearchDraft[columnId] || '' });
      setCommittedColQ(next);
      prevCommittedColQRef.current = emptyDeskColumnQueries();
      return;
    }
    setCommittedColQ((prev) => ({ ...prev, [columnId]: trimmed }));
  };

  const handlePageSearchChange = (value) => {
    setPageSearchDraft(value);
  };

  const handleColumnSearchChange = (columnId, value) => {
    if (committedPageQ.trim() || pageSearchDraft.trim()) {
      setPageSearchDraft('');
      setCommittedPageQ('');
      setCommittedColQ(emptyDeskColumnQueries());
      prevCommittedColQRef.current = emptyDeskColumnQueries();
      setColumnSearchDraft({ ...emptyDeskColumnQueries(), [columnId]: value });
      return;
    }
    setColumnSearchDraft((prev) => ({ ...prev, [columnId]: value }));
  };

  const handleClearSearch = () => {
    setPageSearchDraft('');
    setCommittedPageQ('');
    setColumnSearchDraft(emptyDeskColumnQueries());
    setCommittedColQ(emptyDeskColumnQueries());
    prevCommittedColQRef.current = emptyDeskColumnQueries();
  };

  const handleSearchSizeChange = (nextSize) => {
    setSearchSize(nextSize);
  };

  const handleSearchDaysChange = (nextDays) => {
    setSearchDays(nextDays);
  };

  const handleSearchModeChange = (nextMode) => {
    setSearchMode(nextMode);
  };

  const pageSearchPending =
    pageSearchDraft.trim() !== committedPageQ.trim();

  const activeDeskQuery = resolveActiveDeskQuery(committedPageQ, committedColQ);
  /** HYB-30 — Map on Web CTA stays available in all modes including Graph. */
  const webExploreUrl = activeDeskQuery ? buildWebGraphUrl(activeDeskQuery) : null;

  const resultsLabel = loading && deskSearchActive
    ? 'Searching…'
    : deskSearchActive
      ? `${searchMeta.total} Result${searchMeta.total === 1 ? '' : 's'}`
      : `${searchSize} per column`;

  const sizeMenuOptions = DESK_SEARCH_SIZES.map((size) => ({
    value: size,
    label: `${size} per column`,
    selected: size === searchSize,
  }));

  const modeMenuOptions = DESK_SEARCH_MODES.map((mode) => ({
    value: mode,
    label: deskSearchModeLabel(mode),
    selected: mode === searchMode,
  }));

  const effectiveColumnQuery = (columnId) => {
    const pageQ = committedPageQ.trim();
    if (pageQ) return pageQ;
    return (committedColQ[columnId] || '').trim();
  };

  const columnSearchPending = (columnId) =>
    !pageSearchActive
    && (columnSearchDraft[columnId] || '').trim() !== (committedColQ[columnId] || '').trim();

  const autoSearchRefreshing = DESK_COLUMNS.some((col) => autoSearchPendingMap[col.id]);
  const anyColumnFetchPending = DESK_COLUMNS.some((col) => columnFetchPendingMap[col.id]);
  // Prefer per-column pending UI over full-page spinner (PERF-QT-02).
  const showPageLoading =
    loading && Object.keys(columnData).length === 0 && !anyColumnFetchPending;

  return (
    <div className="desk-page">
      {error && Object.keys(columnData).length === 0 && (
        <PageError
          title={deskSearchActive ? 'Desk search unavailable' : 'Desk unavailable'}
          message={error}
          onRetry={deskSearchActive ? loadDeskSearch : loadDeskDefault}
        />
      )}
      {showPageLoading && (
        <PageLoading message={deskSearchActive ? 'Searching desk columns…' : 'Loading desk feeds…'} />
      )}

      <div className="desk-chrome">
        <div className="desk-body">
          <form
            className="desk-search-bar"
            onSubmit={(event) => {
              event.preventDefault();
              commitPageSearch();
            }}
            role="search"
          >
            <label htmlFor="desk-search" className="desk-search-label">
              Desk search
            </label>
            <input
              id="desk-search"
              type="text"
              className="desk-search-input"
              value={pageSearchDraft}
              onChange={(event) => handlePageSearchChange(event.target.value)}
              placeholder="Search all columns — press Enter"
              autoComplete="off"
              enterKeyHint="search"
            />
            <button type="submit" className="desk-search-submit">
              Search
            </button>
            {(pageSearchDraft || committedPageQ || anyColumnSearchVisible) && (
              <button type="button" className="desk-search-clear" onClick={handleClearSearch}>
                Clear
              </button>
            )}
            {pageSearchPending && (
              <span className="desk-search-pending">Press Enter to search all columns</span>
            )}
            <div className="desk-toolbar-controls">
              <DeskSearchMenu
                label="Search type"
                valueLabel={deskSearchModeLabel(searchMode)}
                options={modeMenuOptions}
                onSelect={handleSearchModeChange}
                disabled={loading && deskSearchActive}
              />
              <DeskSearchMenu
                label="Results per column"
                valueLabel={resultsLabel}
                options={sizeMenuOptions}
                onSelect={handleSearchSizeChange}
                disabled={loading && deskSearchActive}
              />
              {webExploreUrl && (
                <Link to={webExploreUrl} className="desk-search-web-link">
                  Map on Web
                </Link>
              )}
              <div
                className="desk-toolbar-group desk-toolbar-group--divider"
                role="group"
                aria-label="Time window"
              >
                {WINDOW_OPTIONS.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    className={`desk-toolbar-pill ${searchDays === option.days ? 'is-active' : ''}`}
                    aria-pressed={searchDays === option.days}
                    disabled={loading && deskSearchActive}
                    onClick={() => handleSearchDaysChange(option.days)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className={`desk-ctrl-btn desk-live-toolbar-btn ${paused ? '' : 'desk-ctrl-btn--live'}`}
                onClick={() => setPaused((p) => !p)}
                title={paused ? 'Resume live refresh' : 'Pause live refresh'}
                aria-pressed={!paused}
              >
                <span className={`desk-live-dot ${paused ? '' : 'desk-live-dot--pulse'}`} aria-hidden="true" />
                {paused ? 'Paused' : 'LIVE'}
              </button>
            </div>
          </form>
          <SearchKittBar
            active={loading || autoSearchRefreshing || anyColumnFetchPending}
            label={autoSearchRefreshing ? 'Refreshing desk column' : 'Searching desk columns'}
          />

          {deskSearchActive ? (
            <div className="desk-strip desk-strip--search" aria-live="polite">
              <span className="desk-strip-label">DESK QUERY</span>
              <span className="desk-strip-headline desk-strip-query">
                {pageSearchActive ? committedPageQ.trim() : 'Per-column filters'}
              </span>
              <span className="desk-strip-source">
                {pageSearchActive
                  ? `Page search · ${deskSearchModeLabel(searchMode)} · ${searchSize} per column · ${windowLabel(searchDays)}`
                  : `Column search · ${deskSearchModeLabel(searchMode)} · ${searchSize} per column · ${windowLabel(searchDays)}`}
              </span>
            </div>
          ) : (
            stripItem && (
            <div className="desk-strip" aria-live="polite">
              <span className="desk-strip-label">GLOBAL SIGNAL</span>
              {stripColumn ? (
                <span className="desk-strip-cat" style={{ color: `var(${stripColumn.accentVar})` }}>
                  {stripColumn.shortLabel}
                </span>
              ) : (
                <span className="desk-strip-cat desk-strip-cat--neutral">Signal</span>
              )}
              {stripStoryCats ? (
                <span className="desk-strip-story-cats" title="Story categories">
                  {stripStoryCats}
                </span>
              ) : null}
              <Link to={`/article/${stripItem.id}`} className="desk-strip-headline">
                {stripItem.title}
              </Link>
              {isDeskArticleNew(stripItem.publishedAt) && <DeskNewBadge />}
              {webEligibleIds.has(stripItem.id) ? (
                <Link
                  to={buildWebFromArticleUrl(stripItem.id, { layout: 'starburst', mode: 'intersect' })}
                  className="desk-badge-web desk-strip-web"
                  title="Open Memetic Web with all entities from this article"
                >
                  Web
                </Link>
              ) : null}
              <span className="desk-strip-source">{stripItem.source || stripItem.feedName}</span>
              <DeskStripDots
                count={stripArticles.length}
                activeIndex={stripIndex}
                onSelect={setStripIndex}
              />
            </div>
            )
          )}

          <div className={`desk-grid ${loading && deskSearchActive ? 'desk-grid--loading' : ''}`}>
            {DESK_COLUMNS.map((baseCol, index) => {
              const col = effectiveColumns[index] || baseCol;
              const colQ = effectiveColumnQuery(baseCol.id);
              return (
                <DeskColumn
                  key={baseCol.id}
                  column={col}
                  baseColumn={baseCol}
                  articles={columnData[baseCol.id] || []}
                  columnSearchActive={colQ.length > 0}
                  columnSearchPending={columnSearchPending(baseCol.id)}
                  columnSearchQuery={colQ}
                  columnSearchTerm={columnSearchDraft[baseCol.id] || ''}
                  onColumnSearchChange={handleColumnSearchChange}
                  onColumnSearchCommit={commitColumnSearch}
                  collapsed={Boolean(collapsedMap[baseCol.id])}
                  onToggleCollapsed={handleToggleCollapsed}
                  autoSearchEnabled={autoSearchMap[baseCol.id] === true}
                  autoSearchPending={Boolean(autoSearchPendingMap[baseCol.id])}
                  onToggleAutoSearch={handleToggleAutoSearch}
                  onCategoryPresetSelect={handleCategoryPresetSelect}
                  isAdmin={isDeskAdmin}
                  seedTerms={deskSeeds?.[baseCol.id] || []}
                  onSeedsSaved={handleSeedsSaved}
                  windowDays={searchDays}
                  webEligibleIds={webEligibleIds}
                  columnFetchPending={Boolean(columnFetchPendingMap[baseCol.id])}
                />
              );
            })}
          </div>
        </div>

        {DESK_LENS_ENABLED ? (
          <DeskLensDrawer
            open={lensOpen}
            onToggle={() => setLensOpen((o) => !o)}
            clusters={clusters}
            clustersLoading={clustersLoading}
            clustersError={clustersError}
          />
        ) : null}
      </div>
    </div>
  );
};

export default DeskPage;
